import { TransactionApiTagTypes, TransactionApiUrls } from "@/modules/transaction/common/enum";
import type {
  IChangeStateRequest,
  ITransactionsCredentials,
  ITransactionsResponse,
  ITransactionTypesCredentials,
  ITransferSignBody,
} from "@/modules/transaction/common/types";
import type { BeneficiaryType } from "@/shared/common/enums";
import type { IBeneficiary, IBeneficiaryCreationBody } from "@/shared/common/types";
import { buildRoute, generateQuery } from "@/shared/helpers";
import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "app/axios";

export const transactionApi = createApi({
  reducerPath: "transactionApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: [TransactionApiTagTypes.TransactionHistory],
  endpoints: (builder) => ({
    getTransactionTypes: builder.query<
      string[],
      ITransactionTypesCredentials
    >({
      query: ({ pageIndex, pageSize }) => {
        const query = generateQuery({
          pageIndex,
          pageSize,
        });
        const url = `${TransactionApiUrls.TransactionsTypes}?${query}`;
        return { url };
      },
    }),
    getTransactionNumber: builder.query<string, void>({
      query: () => ({
        url: TransactionApiUrls.TransferTransactionNumber,
      }),
    }),
    createBeneficiary: builder.mutation<string, IBeneficiaryCreationBody>({
      query: (data: IBeneficiaryCreationBody) => ({
        url: `${TransactionApiUrls.Beneficiary}`,
        method: "POST",
        data,
      }),
    }),
    signTransfer: builder.mutation<void, ITransferSignBody>({
      query: (data) => ({
        url: TransactionApiUrls.TransferSign,
        method: "POST",
        data,
      }),
    }),
    changeTransactionState: builder.query<void, IChangeStateRequest>({
      query: ({ data, id }) => {
        const url = buildRoute(TransactionApiUrls.TransactionsState, { id });
        return {
          url,
          method: "PATCH",
          data,
        };
      }
    }),
    getBeneficiaryList: builder.query<IBeneficiary[], BeneficiaryType>({
      query: (type: BeneficiaryType) => ({
        url: `${TransactionApiUrls.Beneficiary}/${type}`,
      }),
    }),
    getUrgentAvailability: builder.query({
      query: () => ({
        url: TransactionApiUrls.TransferUrgentAvailable,
      }),
    }),
    getTransactions: builder.query<
      ITransactionsResponse,
      ITransactionsCredentials
    >({
      query: ({
        pageIndex,
        pageSize,
        transactionType,
        transactionStatus,
        startDate,
        endDate,
        minAmount,
        maxAmount,
        currency,
      }) => {
        const query = generateQuery({
          pageIndex,
          pageSize,
          transactionType,
          transactionStatus,
          startDate,
          endDate,
          minAmount,
          maxAmount,
          currency,
        });
        const url = `${TransactionApiUrls.Transactions}?${query}`;
        return { url };
      },
      providesTags: [TransactionApiTagTypes.TransactionHistory],
    }),
  }),
});

export const {
  useGetTransactionTypesQuery,
  useGetTransactionsQuery,
  useLazyGetTransactionNumberQuery,
  useSignTransferMutation,
  useCreateBeneficiaryMutation,
  useGetUrgentAvailabilityQuery,
  useGetBeneficiaryListQuery,
  useLazyChangeTransactionStateQuery,
} = transactionApi;
